
import { useEffect } from "react";
import { User, BirdieGameGroup, Course, CurrentRound } from "@/types";
import { Button } from "@/components/ui/button";
import { Home, Save } from "lucide-react";
import RecordBirdieModal from "./birdie/RecordBirdieModal";
import { format } from "date-fns";
import HoleGrid from "./scoring/HoleGrid";
import ProgressBars from "./scoring/ProgressBars";
import GolferSelectionModal from "./scoring/GolferSelectionModal";
import { toast } from "@/hooks/use-toast";
import GolferTabs from "./scoring/GolferTabs";
import { useHoleScoring } from "@/hooks/useHoleScoring";

interface ScoringPanelProps {
  golfers: User[];
  course: Course;
  eligibleGames: BirdieGameGroup[];
  onReset: () => void;
  gameId?: string;
}

const ScoringPanel = ({ golfers, course, eligibleGames, onReset, gameId }: ScoringPanelProps) => {
  const {
    activeGolferId,
    setActiveGolferId,
    selectedHole,
    selectedGolfer,
    isRecordModalOpen,
    isGolferSelectOpen,
    refreshKey,
    holeTileData,
    filteredGolfers,
    currentGameId,
    currentGolferGameIds,
    handleHoleClick,
    handleGolferSelect,
    handleBirdieRecorded,
    closeModal,
  } = useHoleScoring({ golfers, course, eligibleGames, gameId, mode: 'scoring' });

  useEffect(() => {
    const activeRound: CurrentRound = {
      golfers,
      courseId: course.id,
      date: new Date().toISOString(),
      eligibleGames,
    };
    localStorage.setItem('activeRound', JSON.stringify(activeRound));
  }, [golfers, course, eligibleGames]);

  const handleFinishScoring = () => {
    localStorage.removeItem('activeRound');
    toast({
      title: "Scoring completed",
      description: "Your scoring session has been completed and saved.",
    });
    onReset();
  };

  return (
    <div className="px-4 pb-20">
      <div className="flex items-center justify-between gap-2 mb-4">
        <Button
          variant="outline"
          className="flex items-center gap-1 shrink-0"
          onClick={onReset}
        >
          <Home className="h-4 w-4" />
          Back
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-1 shrink-0 bg-green-50 border-green-200 text-green-600 hover:bg-green-100"
          onClick={handleFinishScoring}
        >
          <Save className="h-4 w-4" />
          Finish
        </Button>
        <div className="text-sm font-medium min-w-0 truncate text-right">
          {format(new Date(), "MMM d, yyyy")}
        </div>
      </div>

      {golfers.length > 1 && (
        <GolferTabs
          golfers={golfers}
          selectedGolferId={activeGolferId}
          onGolferChange={setActiveGolferId}
        />
      )}

      <ProgressBars
        golfers={filteredGolfers}
        eligibleGames={eligibleGames}
        courseId={course.id}
        refreshKey={refreshKey}
        totalHoles={course.holes.length}
      />

      <div className="golf-pattern-bg p-4 rounded-lg shadow-inner">
        <HoleGrid
          holes={course.holes}
          holeTileData={holeTileData}
          activeGolferId={activeGolferId}
          onHoleClick={handleHoleClick}
        />
      </div>

      <GolferSelectionModal
        isOpen={isGolferSelectOpen}
        golfers={golfers}
        selectedHole={selectedHole}
        onGolferSelect={handleGolferSelect}
        onCancel={() => closeModal()}
      />

      {isRecordModalOpen && selectedGolfer && selectedHole && (
        <RecordBirdieModal
          isOpen={isRecordModalOpen}
          onClose={closeModal}
          onSuccess={handleBirdieRecorded}
          course={course}
          userId={selectedGolfer.id}
          selectedHole={selectedHole}
          gameId={currentGameId}
          gameIds={currentGolferGameIds}
          gamePlayers={golfers}
        />
      )}
    </div>
  );
};

export default ScoringPanel;
